// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { ago } = require('shared/date')
const {
  ARTICLE_SUBTYPES_KEYS: articleSubtypeKeys
} = require('shared/model-types')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { eventHandler } = require('client-v2/common/utils/events')
const useSymbol = require('client-v2/common/hooks/symbol')
const {
  ArticleAddCircleButton,
  CircleButton
} = require('client-v2/common/components/buttons')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
const AddToCollectionModalAnchor = require('client-v2/common/containers/lightweight-engagement/containers/add-to-collection-modal/components/anchor-smart')
const { CheckExperiment } = require('client-v2/common/containers/rollouts')
const { articleDomainSearchHref } = require('client-v2/common/models/article/actions')
const { userProfileHref } = require('client-v2/common/models/user/actions')
// Container imports
const { TabNameEnum } = require('../state/slide-record')
const ArticleProfileCollections = require('./collections-smart')
const ArticleProfileComments = require('./comments-smart')
const ArticleProfileInterests = require('./interests-smart')

// BEM Block entity definition
const bem = BemEntity('ArticleProfileDetailCard')

function ArticleProfileDetailCard(props) {
  const { article, slideIndex } = props
  const [isEmbedDisclosed, setEmbedDisclosed] = React.useState(false)
  const onEmbedDisclose = React.useCallback(() => setEmbedDisclosed(true))
  return (
    tag('article', {
      className: classNames(
        props.className,
        bem.block({
          // Prefer the subtype enum key in the stylesheet instead of the value
          subtype: articleSubtypeKeys[article.subtype]
        })
      )
    },
      tag(Curator, props),
      tag('div', {
        className: bem.element('contents')
      },
        tag('div', {
          className: bem.element('main-content')
        },
          tag('div', {
            className: bem.element('article-info')
          },
            tag(ArticleImage, {
              ...props,
              isEmbedDisclosed,
              onEmbedDisclose
            }),
            tag(ArticleActions, {
              ...props,
              isEmbedDisclosed
            }),
            tag(ArticleDomain, props),
            tag(ArticleTitle, props),
            tag(ArticleDescription, props)
          ),

          tag('div', {
            className: bem.element('comments-content')
          },
            tag(ArticleProfileComments, {
              slideIndex
            })
          ),

          tag(Tabs, props)
        ),

        tag('div', {
          className: bem.element('sidebar-content')
        },
          tag(ArticleProfileCollections, {
            isScrollable: true,
            slideIndex
          }),
          tag(ArticleProfileInterests, {
            slideIndex
          })
        )
      )
    )
  )
}

const tabPropTypes = {
  count: PropTypes.number,
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired
}

ArticleProfileDetailCard.propTypes = {
  // Flags
  isEmbedDisclosed: PropTypes.bool,
  // Values
  article: PropTypes.object.isRequired,
  className: PropTypes.string,
  curatedAt: PropTypes.string,
  curatedBy: PropTypes.object,
  slideIndex: PropTypes.number,
  tabs: PropTypes.shape({
    [TabNameEnum.COLLECTIONS]: PropTypes.shape(tabPropTypes),
    [TabNameEnum.COMMENTS]: PropTypes.shape(tabPropTypes),
    [TabNameEnum.INTERESTS]: PropTypes.shape(tabPropTypes)
  }).isRequired,
  // Callbacks
  onAddClick: PropTypes.func,
  onContentClick: PropTypes.func,
  onDomainClick: PropTypes.func,
  onMoreActionsClick: PropTypes.func,
  onShareClick: PropTypes.func,
  onTabSelect: PropTypes.func,
  onUserNavigate: PropTypes.func
}

ArticleProfileDetailCard.defaultProps = {
  isEmbedDisclosed: false
}

module.exports = ArticleProfileDetailCard

// BEM Block entity Element components

function Curator(props) {
  const { curatedAt, curatedBy } = props
  if (!curatedBy) {
    // Show the empty space where the curator should be
    return tag('div', {
      className: bem.element('curator')
    })
  }
  let curatorTime = null
  if (curatedAt) {
    curatorTime = tag('time', {
      className: bem.element('curator-time'),
      dateTime: curatedAt
    },
      ago(curatedAt)
    )
  }
  const onUserClick = eventHandler(props.onUserNavigate, { user: curatedBy })
  return (
    tag('div', {
      className: bem.element('curator')
    },
      tag(UserAvatar, {
        className: bem.element('curator-icon'),
        href: true,
        user: curatedBy,
        onClick: onUserClick
      }),
      tag('div', {
        className: bem.element('curator-text')
      },
        'First added by ',
        tag('a', {
          className: bem.element('curator-href'),
          href: userProfileHref(curatedBy),
          onClick: onUserClick
        },
          `@${curatedBy.username}`
        ),
        curatorTime
      )
    )
  )
}

function ArticleImage(props) {
  const { article } = props
  const { embedUrl, image, md5image, url } = article
  // Render an embedded player if the article has an embed URL
  let onImageClick = eventHandler(props.onContentClick, { article })
  let embeddedPlayer
  if (embedUrl) {
    onImageClick = null
    embeddedPlayer = (
      tag(EmbeddedPlayer, {
        isDisclosed: props.isEmbedDisclosed,
        className: bem.element('embed'),
        src: embedUrl,
        onDisclose: props.onEmbedDisclose
      })
    )
  }
  return (
    tag('div', {
      className: bem.element('image-container')
    },
      tag(LazyImage, {
        className: bem.element('image'),
        href: url,
        rel: 'nofollow noopener',
        target: '_blank',
        sources: [md5image, image],
        onClick: onImageClick
      }),
      embeddedPlayer
    )
  )
}

function ArticleActions(props) {
  const anchorId = useSymbol()
  const { article } = props
  return (
    tag('div', {
      className: bem.element('actions', {
        'shift-down': props.isEmbedDisclosed
      })
    },
      /**
       * Don't show more actions on your own posts
       * because you can't report them.
       * @see https://app.asana.com/0/102902769366319/1111869557372309/f
       * The only other action would be sharing,
       * but there's already a button for that below.
       */
      !article.hasMix && tag(CircleButton, {
        className: bem.element('action-button', 'more'),
        title: 'Show more actions for this article',
        onClick: () => props.onMoreActionsClick({ article, anchorId })
      }),
      tag(CircleButton, {
        className: bem.element('action-button', 'share'),
        title: 'Share this article with a friend',
        onClick: () => props.onShareClick({ article })
      }),
      tag(CheckExperiment, {
        experimentKey: '*lightweight_engagement',
        renderWithModifier: modifier => {
          if (modifier === 'disabled') {
            return (
              tag(ArticleAddCircleButton, {
                className: bem.element('action-button', 'add'),
                checked: props.article.hasMix,
                onClick: () => props.onAddClick({ article })
              })
            )
          }
          return (
            tag(AddToCollectionModalAnchor, {
              placement: 'bottom-end',
              urlId: props.article['url_id'],
              anchorId
            },
              tag(ArticleMixButton, {
                className: bem.element('action-button', 'mix'),
                checked: props.article.hasMix,
                onClick: () => props.onAddClick({ article, anchorId })
              })
            )
          )
        }
      })
    )
  )
}

function ArticleDomain(props) {
  const { article } = props
  return (
    tag('a', {
      className: bem.element('domain'),
      href: articleDomainSearchHref(article),
      title: article.hostname,
      onClick: eventHandler(props.onDomainClick, { article })
    },
      article.hostname
    )
  )
}

function ArticleTitle(props) {
  const { article } = props
  return (
    tag('a', {
      className: bem.element('title'),
      href: article.url,
      rel: 'nofollow noopener',
      target: '_blank',
      title: article.title,
      onClick: eventHandler(props.onContentClick, { article })
    },
      article.title
    )
  )
}

function ArticleDescription(props) {
  const { article } = props
  return (
    tag('a', {
      className: bem.element('description'),
      href: article.url,
      rel: 'nofollow noopener',
      target: '_blank',
      title: article.description,
      onClick: eventHandler(props.onContentClick, { article })
    },
      article.description
    )
  )
}

function Tabs(props) {
  const { article, slideIndex, tabs } = props

  return (
    tag('div', {
      className: bem.element('tabs-content')
    },
      tag('nav', {
        className: bem.element('nav')
      },
        tag('ul', {
          className: bem.element('tabs')
        },
          renderTab(TabNameEnum.COLLECTIONS),
          renderTab(TabNameEnum.COMMENTS),
          renderTab(TabNameEnum.INTERESTS)
        )
      ),
      renderSelectedTab()
    )
  )

  function onSelect(selectedTab) {
    props.onTabSelect({ article, selectedTab })
  }

  function renderTab(name) {
    return tag(Tab, {
      ...tabs[name],
      name: name,
      onSelect
    })
  }

  function renderSelectedTab() {
    if (tabs[TabNameEnum.COLLECTIONS].isSelected) {
      return tag(ArticleProfileCollections, { slideIndex })
    }
    if (tabs[TabNameEnum.COMMENTS].isSelected) {
      return tag(ArticleProfileComments, { slideIndex })
    }
    if (tabs[TabNameEnum.INTERESTS].isSelected) {
      return tag(ArticleProfileInterests, { slideIndex })
    }
  }
}

function Tab(props) {
  // Hide the interests tab if there is nothing to show
  if (props.name === TabNameEnum.INTERESTS) {
    if (!props.isLoading && !props.count) {
      return null
    }
  }
  // Only allow tab selection once they have finished loading
  const onClick = props.isLoading ? null : () => props.onSelect(props.name)
  return (
    tag('li', {
      className: bem.element('tab', {
        [props.name]: true,
        interactive: !!onClick,
        loading: props.isLoading,
        selected: props.isSelected
      }),
      title: props.label,
      onClick
    },
      props.label
    )
  )
}
